import { z } from "zod";
import { PaymentCodesArray } from "../views/CustomerView/customerTypes";
import { TimeRestrictionDayOfWeek } from "./types";
import {
    digitsOnlyInvalidMessage,
    digitsOnlyPattern,
    emailInvalidMessage,
    getIsFieldRequiredMessage,
    isNoDigitsPattern,
    medmeraIdInvalidMessage,
    mobileNrPattern,
    mobileNumberInvalidMessage,
    noDigitsInvalidMessage,
    postalCodeInvalidMessage,
    postalCodePattern,
    ssnInvalidMessage,
    ssnPattern,
} from "./validations";

const requiredError = (message: string) => ({
    error: (issue: { input: unknown }) =>
        issue.input === undefined ? message : undefined,
});

const emailRequiredMessage = getIsFieldRequiredMessage("E-postadress");
export const emailSchema = z
    .string(requiredError(emailRequiredMessage))
    .trim()
    .min(1, { error: emailRequiredMessage })
    .pipe(z.email({ error: emailInvalidMessage }));

export const optionalEmailSchema =
    // https://github.com/colinhacks/zod/issues/310#issuecomment-794533682
    emailSchema.optional().or(z.literal(""));

const firstNameRequiredMessage = getIsFieldRequiredMessage("Förnamn");
export const firstNameSchema = z
    .string(requiredError(firstNameRequiredMessage))
    .trim()
    .min(1, { error: firstNameRequiredMessage })
    .regex(isNoDigitsPattern, { error: noDigitsInvalidMessage });

const lastNameRequiredMessage = getIsFieldRequiredMessage("Efternamn");
export const lastNameSchema = z
    .string(requiredError(lastNameRequiredMessage))
    .trim()
    .min(1, { error: lastNameRequiredMessage })
    .regex(isNoDigitsPattern, { error: noDigitsInvalidMessage });

const addressLineRequiredMessage = getIsFieldRequiredMessage("Gatuadress");
export const addressLineSchema = z
    .string(requiredError(addressLineRequiredMessage))
    .trim()
    .min(1, { error: addressLineRequiredMessage });

const postalCodeRequiredMessage = getIsFieldRequiredMessage("Postnummer");
export const postalCodeSchema = z
    .string(requiredError(postalCodeRequiredMessage))
    .trim()
    .min(1, { error: postalCodeRequiredMessage })
    .regex(postalCodePattern, { error: postalCodeInvalidMessage });

const mobilePhoneRequiredMessage = getIsFieldRequiredMessage("Mobilnummer");
export const mobilePhoneRequiredSchema = z
    .string(requiredError(mobilePhoneRequiredMessage))
    .trim()
    .min(1, { error: mobilePhoneRequiredMessage })
    .regex(mobileNrPattern, { error: mobileNumberInvalidMessage });

export const mobilePhoneNotRequiredSchema = z.union([
    z.literal(""),
    mobilePhoneRequiredSchema,
]);

export const homePhoneSchema = z.string().regex(digitsOnlyPattern, {
    error: digitsOnlyInvalidMessage,
});

const townRequiredMessage = getIsFieldRequiredMessage("Ort");
export const townSchema = z
    .string(requiredError(townRequiredMessage))
    .trim()
    .min(1, { error: townRequiredMessage })
    .regex(isNoDigitsPattern, { error: noDigitsInvalidMessage });

export const ssnRequiredMessage = getIsFieldRequiredMessage("Personnummer");
export const ssnSchema = z
    .string(requiredError(ssnRequiredMessage))
    .trim()
    .min(1, { error: ssnRequiredMessage })
    .regex(ssnPattern, {
        error: ssnInvalidMessage,
    });

const unitRequiredMessage = getIsFieldRequiredMessage("Enhet");
export const unitSchema = z.object(
    { id: z.string(), name: z.string() },
    requiredError(unitRequiredMessage)
);

const timeRestrictionsRequiredMessage =
    getIsFieldRequiredMessage("Leveranstid");
export const timeRestrictionsSchema = z
    .array(
        z.object({
            id: z.string(),
            name: z.string(),
            extraData: z.object({
                dayOfWeek: z.enum(TimeRestrictionDayOfWeek),
                startTime: z.string(),
                endTime: z.string(),
                replacementHint: z.boolean().optional(),
            }),
        }),
        requiredError(timeRestrictionsRequiredMessage)
    )
    .min(1, { error: timeRestrictionsRequiredMessage });

export const medmeraIdSchema = z.union([
    // https://github.com/colinhacks/zod/issues/2513#issuecomment-1732405993
    z.literal(""),
    z.string().trim().length(7, { error: medmeraIdInvalidMessage }),
]);

const paymentRequiredMessage = getIsFieldRequiredMessage("Betalsätt");
export const allPaymentsSchema = z.enum(
    PaymentCodesArray,
    requiredError(paymentRequiredMessage)
);

export const invoicePaymentSchema = z.enum(
    ["collectiveinvoice", "intrum"],
    requiredError(paymentRequiredMessage)
);

export const matkontoPaymentSchema = z.literal("matkonto");
